import { useCallback, useEffect, useState } from 'react'
import BackRow, { BackButton } from './BackButton'
import Icon from '../components/Icon'
import { imageUrl, worx, type Applicant, type Category, type JobCard, type Membership } from './client'
import { Logo, initials } from './Jobs'
import Report from './Report'
import { ago, categoryName, payLabel, payShort, tw } from './strings'
import { celebrate, done } from './feel'

/** Matches the CHECK constraint in 0106 and TAGLINE_MAX on the server. */
const TAGLINE_MAX = 48
/** The same threshold the server badges a job with. See worx/matching.ts. */
const MATCH_MIN = 3

const ARRANGEMENTS = ['full_time', 'part_time', 'temporary', 'seasonal', 'gig'] as const
const PAY_UNITS = ['hour', 'day', 'salary', 'contract', 'tipped'] as const
const STATUSES = ['viewed', 'contacted', 'interview', 'hired', 'not_selected'] as const

/* ── Registration ─────────────────────────────────────────────────────────── */

/**
 * Registering a business.
 *
 * Short, because the gate is verification rather than the form: anything asked
 * for here that an administrator will not actually check is a field that only
 * costs the honest employer time.
 */
export function Register({ onRegistered }: { onRegistered: (m: Membership) => void }) {
  const [form, setForm] = useState({ name: '', contactName: '', categoryId: '', phone: '', email: '', aboutEn: '', tagline: '', proof: '' })
  const [categories, setCategories] = useState<Category[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void worx.categories().then((c) => setCategories(c.categories)).catch(() => {}) }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { employer } = await worx.registerEmployer(form)
      celebrate({ title: tw('w.cheer.registered'), line: tw('w.cheer.registeredLine') })
      onRegistered(employer)
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.save'))
    } finally {
      setBusy(false)
    }
  }

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <form className="w-form w-register" onSubmit={submit}>
      <header className="w-register-head">
        <span className="w-register-ic" aria-hidden="true"><Icon name="briefcase" size={28} /></span>
        <div>
          <h2>{tw('w.biz.register')}</h2>
          <p className="muted">{tw('w.biz.intro')}</p>
        </div>
      </header>

      <section className="card w-register-sec">
        <h3>{tw('w.biz.secBusiness')}</h3>
        <div className="w-register-grid">
          <label>
            <span className="w-label">{tw('w.biz.name')} <span className="w-required">*</span></span>
            <input value={form.name} onChange={set('name')} required maxLength={120} />
          </label>
          <label>
            {tw('w.biz.category')}
            <select value={form.categoryId} onChange={set('categoryId')}>
              <option value="">—</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{categoryName(c)}</option>)}
            </select>
          </label>
        </div>
        <label>
          {tw('w.biz.tagline')}
          <input
            value={form.tagline}
            onChange={set('tagline')}
            maxLength={TAGLINE_MAX}
            placeholder={tw('w.biz.taglineExample')}
          />
          {/* The counter is not decoration: the cap is a LAYOUT limit — one row
              on every job card — so somebody has to be able to see themselves
              approaching it rather than being cut off at submit. */}
          <span className="muted small">
            {tw('w.biz.taglineHint')} · {form.tagline.length}/{TAGLINE_MAX}
          </span>
        </label>
        <label>
          {tw('w.biz.about')}
          <textarea value={form.aboutEn} onChange={set('aboutEn')} rows={3} maxLength={2000} />
        </label>
      </section>

      <section className="card w-register-sec">
        <h3>{tw('w.biz.secReach')}</h3>
        <label>
          {tw('w.biz.contact')}
          <input value={form.contactName} onChange={set('contactName')} maxLength={120} />
        </label>
        <div className="w-register-grid">
          <label>
            {tw('w.biz.phone')}
            <input type="tel" value={form.phone} onChange={set('phone')} maxLength={40} />
          </label>
          <label>
            {tw('w.biz.email')}
            <input type="email" value={form.email} onChange={set('email')} maxLength={200} />
          </label>
        </div>
      </section>

      <section className="card w-register-sec">
        <h3>{tw('w.biz.secProof')}</h3>
        <label>
          <span className="w-label">{tw('w.biz.proof')} <span className="w-required">*</span></span>
          <textarea value={form.proof} onChange={set('proof')} rows={3} maxLength={2000} required />
          <span className="muted small">{tw('w.biz.proofHint')}</span>
        </label>
      </section>

      {error && <p className="error" role="alert">{error}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? '…' : tw('w.biz.submit')}</button>
    </form>
  )
}

/* ── Verification banner ──────────────────────────────────────────────────── */

/**
 * The one thing an unverified employer needs to know, said without stopping
 * them working.
 *
 * `pending` reads as an invitation rather than a wall, because that is the
 * truth: drafts can be written now and go out the moment approval lands. A
 * screen that simply said "not verified" would have people close the app and
 * come back never.
 */
export function Standing({ membership, onReviewBusiness }: { membership: Membership; onReviewBusiness?: () => void }) {
  if (membership.status === 'verified') return null
  const message = membership.status === 'pending' ? tw('w.biz.pending')
    : membership.status === 'rejected' ? tw('w.biz.rejected')
    : tw('w.biz.suspended')
  return <div className="w-guidance" role="status"><p>{message}</p>{onReviewBusiness && <button type="button" onClick={onReviewBusiness}>{tw('w.guidance.reviewBusiness')}</button>}</div>
}

/* ── Job posts ────────────────────────────────────────────────────────────── */

/**
 * The employer's own postings.
 *
 * ── Three piles, and why paused is not one of them ──────────────────────────
 *
 * Active, Drafts, Closed. Paused sits in Active wearing its own pill, because
 * a paused posting is a live posting that is temporarily not accepting — the
 * employer's mental model is "this job is still a thing", and a fourth tab
 * holding one row most of the time is a tab nobody opens.
 *
 * ── The row's action is the errand, not the object ──────────────────────────
 *
 * A published posting's button is "View Applicants", because that is the only
 * reason anybody opens this screen twice. A draft's is "Finish & Publish", in
 * amber, because a draft is unfinished work and the app should say so. A
 * closed one has no button at all: everything left to do with it lives behind
 * the menu.
 */
export function Postings({
  membership,
  onApplicants,
  startNew = false,
  onStartedNew,
  onReviewBusiness,
}: {
  membership: Membership
  onApplicants: (jobId: string) => void
  /** Set when the dashboard's "Post a Job" brought us here. */
  startNew?: boolean
  onStartedNew?: () => void
  onReviewBusiness?: () => void
}) {
  const [jobs, setJobs] = useState<JobCard[] | null>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(startNew ? 'new' : null)
  const [pile, setPile] = useState<'active' | 'drafts' | 'closed'>('active')
  const [q, setQ] = useState('')
  const [menu, setMenu] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setJobs((await worx.employerJobs(membership.employerId)).jobs)
    } catch {
      setJobs([])
    }
  }, [membership.employerId])

  useEffect(() => { void load() }, [load])

  // The flag is consumed once, so returning to this tab later does not
  // reopen an empty editor.
  useEffect(() => { if (startNew) onStartedNew?.() }, [startNew, onStartedNew])

  if (editing) {
    return (
      <>
      <Standing membership={membership} onReviewBusiness={onReviewBusiness} />
      <Editor
        employerId={membership.employerId}
        jobId={editing === 'new' ? null : editing}
        onDone={() => { setEditing(null); void load() }}
      /></>
    )
  }

  const all = jobs ?? []
  const counts = {
    active: all.filter((j) => pileOfJob(j) === 'active').length,
    drafts: all.filter((j) => pileOfJob(j) === 'drafts').length,
    closed: all.filter((j) => pileOfJob(j) === 'closed').length,
  }
  const needle = q.trim().toLowerCase()
  const shown = all
    .filter((j) => pileOfJob(j) === pile)
    .filter((j) => !needle || j.title.toLowerCase().includes(needle))

  const open = menu ? all.find((j) => j.id === menu) ?? null : null

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{tw('w.post.screenTitle')}</h1>
      <Standing membership={membership} onReviewBusiness={onReviewBusiness} />

      {/* The dashboard's words, not the editor's: this is the same errand and
          it should be the same button wherever somebody meets it. */}
      <button className="primary w-post" onClick={() => setEditing('new')}>
        <Icon name="plus" size={20} /> {tw('w.dash.post')}
      </button>

      <div className="w-seg" role="tablist">
        {(['active', 'drafts', 'closed'] as const).map((p) => (
          <button
            key={p}
            role="tab"
            className={pile === p ? 'w-seg-b on' : 'w-seg-b'}
            aria-selected={pile === p}
            onClick={() => setPile(p)}
          >
            {p === 'active' ? tw('w.post.active') : p === 'drafts' ? tw('w.post.drafts') : tw('w.post.closedTab')}
            <span className="w-seg-n">{counts[p]}</span>
          </button>
        ))}
      </div>

      <div className="w-search">
        <Icon name="search" size={20} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tw('w.post.search')}
          aria-label={tw('w.post.search')}
        />
      </div>

      {jobs === null && <p className="muted">…</p>}
      {jobs !== null && shown.length === 0 && (
        <p className="muted w-empty">{all.length ? tw('w.post.noneHere') : tw('w.post.none')}</p>
      )}

      {shown.map((j) => (
        <PostingCard
          key={j.id}
          job={j}
          onApplicants={() => onApplicants(j.id)}
          onEdit={() => setEditing(j.id)}
          onMenu={() => setMenu(j.id)}
        />
      ))}

      {/* The lifecycle lives in a sheet rather than on the card: publishing,
          pausing, filling and closing are four decisions that belong together,
          and four text links per row turned the list into a control panel. */}
      {open && (
        <>
          <div className="w-scrim" onClick={() => setMenu(null)} aria-hidden="true" />
          <div className="w-sheet" role="dialog" aria-modal="true" aria-label={tw('w.post.manage')}>
            <header>
              <h2>{open.title}</h2>
              <button className="link" onClick={() => setMenu(null)}>{tw('w.feed.done')}</button>
            </header>
            <div className="w-opts">
              <button className="w-opt" onClick={() => { setMenu(null); setEditing(open.id) }}>
                <Icon name="pencil" size={18} /> {tw('w.post.edit')}
              </button>
              {open.status !== 'draft' && (
                <button className="w-opt" onClick={() => { setMenu(null); onApplicants(open.id) }}>
                  <Icon name="people" size={18} /> {tw('w.tab.applicants')}
                </button>
              )}
              <Lifecycle job={open} onChanged={async () => { setMenu(null); await load() }} onReviewBusiness={() => { setMenu(null); onReviewBusiness?.() }} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** Which of the three piles a posting is in. */
function pileOfJob(job: JobCard): 'active' | 'drafts' | 'closed' {
  if (job.status === 'draft') return 'drafts'
  if (job.status === 'published' || job.status === 'paused') return 'active'
  return 'closed'
}

function PostingCard({
  job, onApplicants, onEdit, onMenu,
}: {
  job: JobCard
  onApplicants: () => void
  onEdit: () => void
  onMenu: () => void
}) {
  const draft = job.status === 'draft'
  const closed = pileOfJob(job) === 'closed'

  return (
    <article className="w-jcard w-pcard">
      <Logo name={job.employerName} src={job.employerLogoId} className="w-jcard-logo" />

      <div className="w-jcard-main">
        <div className="w-jcard-top">
          <h3 className="w-jcard-title">{job.title}</h3>
          <span className={`w-stat w-stat-job-${job.status}`}>
            <span className="w-stat-dot" aria-hidden="true" />
            {tw(`w.jobstatus.${job.status}` as never)}
          </span>
        </div>

        <ul className="w-cfacts w-cfacts-1">
          <li className="w-cfact">
            <span className="w-cfact-ic"><Icon name="money" size={16} /></span>
            {payShort(job)}
          </li>
          {job.arrangement && (
            <li className="w-cfact">
              <span className="w-cfact-ic"><Icon name="briefcase" size={16} /></span>
              {tw(`w.arr.${job.arrangement}` as never)}
            </li>
          )}
          <li className="w-cfact">
            <span className="w-cfact-ic"><Icon name="people" size={16} /></span>
            {job.applicants ?? 0} {(job.applicants ?? 0) === 1 ? tw('w.ap.one') : tw('w.ap.many')}
            {/* Green, and only when there are any: this is the number that
                decides whether this row is worth opening today. */}
            {Boolean(job.unreviewed) && (
              <b className="w-new-count"> · {job.unreviewed} {tw('w.post.newApplicants')}</b>
            )}
          </li>
          <li className="w-cfact">
            <span className="w-cfact-ic"><Icon name="calendar" size={16} /></span>
            {job.publishedAt ? `${tw('w.feed.posted')} ${ago(job.publishedAt)}` : tw('w.jobstatus.draft')}
          </li>
        </ul>
      </div>

      <button className="w-jcard-menu" onClick={onMenu} aria-label={tw('w.post.manage')}>
        <Icon name="more" size={20} />
      </button>

      {!closed && (
        <div className="w-jcard-foot">
          {draft ? (
            <button className="w-cta w-cta-draft" onClick={onEdit}>
              {tw('w.post.finish')} <Icon name="chevron" size={18} />
            </button>
          ) : (
            <button className="w-cta" onClick={onApplicants}>
              {tw('w.post.viewApplicants')} <Icon name="chevron" size={18} />
            </button>
          )}
        </div>
      )}
    </article>
  )
}

/**
 * The buttons that move a posting along.
 *
 * Only the transitions that make sense from where it is now are rendered.
 * Showing "Publish" on a filled job and refusing it on click is a button whose
 * only function is to produce an error message.
 */
function Lifecycle({ job, onChanged, onReviewBusiness }: { job: JobCard; onChanged: () => Promise<void> | void; onReviewBusiness?: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [needsVerification, setNeedsVerification] = useState(false)

  const NEXT: Record<string, { status: string; label: string }[]> = {
    draft: [{ status: 'published', label: tw('w.post.publish') }],
    published: [
      { status: 'paused', label: tw('w.post.pause') },
      { status: 'filled', label: tw('w.post.fill') },
      { status: 'closed', label: tw('w.post.close') },
    ],
    paused: [
      { status: 'published', label: tw('w.post.publish') },
      { status: 'closed', label: tw('w.post.close') },
    ],
    filled: [],
    closed: [{ status: 'published', label: tw('w.post.publish') }],
    removed: [],
  }

  async function go(status: string) {
    setBusy(true)
    setError(null)
    setNeedsVerification(false)
    try {
      await worx.setJobStatus(job.id, status)
      if (status === 'published') celebrate({ title: tw('w.cheer.published'), line: tw('w.cheer.publishedLine') })
      else done()
      await onChanged()
    } catch (err) {
      setNeedsVerification(err instanceof Error && 'code' in err && err.code === 'not_verified')
      setError(err instanceof Error ? err.message : tw('w.error.work'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {(NEXT[job.status] ?? []).map((n) => (
        <button key={n.status} className="link" disabled={busy} onClick={() => void go(n.status)}>
          {n.label}
        </button>
      ))}
      {error && <p className="error small">{error}</p>}
      {needsVerification && onReviewBusiness && <button onClick={onReviewBusiness}>{tw('w.guidance.reviewBusiness')}</button>}
    </>
  )
}

function Editor({
  employerId,
  jobId,
  onDone,
}: {
  employerId: string
  jobId: string | null
  onDone: () => void
}) {
  const [form, setForm] = useState({
    title: '', categoryId: '', description: '', payMin: '', payMax: '', payUnit: 'hour',
    arrangement: '', schedule: '', location: '', startsOn: '', requirements: '', closesOn: '',
  })
  const [categories, setCategories] = useState<Category[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('draft')

  useEffect(() => {
    void worx.categories().then((c) => setCategories(c.categories)).catch(() => {})
    if (!jobId) return
    void worx.job(jobId).then(({ job }) => { setStatus(job.status); setForm({
      title: job.title,
      categoryId: job.categoryId ?? '',
      description: job.description,
      payMin: job.payMin?.toString() ?? '',
      payMax: job.payMax?.toString() ?? '',
      payUnit: job.payUnit ?? 'hour',
      arrangement: job.arrangement ?? '',
      schedule: job.schedule ?? '',
      location: job.location ?? '',
      startsOn: job.startsOn ?? '',
      requirements: job.requirements ?? '',
      closesOn: job.closesOn ?? '',
    }) }).catch(() => setError(tw('w.error.jobLoad')))
  }, [jobId])

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(e: { preventDefault(): void }, publish = false) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const body = {
      ...form,
      // Empty string is "not stated", which is a real answer for pay. Sending
      // it as 0 would publish a job offering nothing an hour.
      payMin: form.payMin === '' ? null : Number(form.payMin),
      payMax: form.payMax === '' ? null : Number(form.payMax),
      startsOn: form.startsOn || null,
      closesOn: form.closesOn || null,
    }
    try {
      // A failed publish leaves the draft saved; remembering its id keeps a
      // retry from creating a second copy.
      let id = jobId ?? savedId
      if (id) await worx.updateJob(id, body)
      else id = (await worx.createJob(employerId, body)).id
      setSavedId(id)
      if (publish) {
        await worx.setJobStatus(id, 'published')
        celebrate({ title: tw('w.cheer.published'), line: tw('w.cheer.publishedLine') })
      } else done()
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.save'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="w-form" onSubmit={submit}>
      <BackRow onBack={onDone}>
        <h2>{jobId ? form.title || tw('w.post.new') : tw('w.post.new')}</h2>
      </BackRow>

      <label>
        <span className="w-label">{tw('w.post.title')} <span className="w-required">*</span></span>
        <input value={form.title} onChange={set('title')} required maxLength={120} />
      </label>

      <label>
        {tw('w.biz.category')}
        <select value={form.categoryId} onChange={set('categoryId')}>
          <option value="">—</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{categoryName(c)}</option>)}
        </select>
      </label>

      <label>
        <span className="w-label">{tw('w.post.description')} <span className="w-required">*</span></span>
        <textarea value={form.description} onChange={set('description')} rows={6} required maxLength={8000} />
      </label>

      {/* The same three captioned fields as the worker's profile, deliberately:
          it is the same sentence — from, to, per — and an employer typing a
          range should not meet a different control from the one the worker
          typed theirs into. */}
      <fieldset>
        <legend>{tw('w.post.pay')}</legend>
        <div className="w-pay-row">
          <label className="w-pay-field">
            <span className="muted small">{tw('w.profile.payFrom')}</span>
            <input type="number" inputMode="numeric" min={0} value={form.payMin} onChange={set('payMin')} />
          </label>
          <label className="w-pay-field">
            <span className="muted small">{tw('w.profile.payTo')}</span>
            <input type="number" inputMode="numeric" min={0} value={form.payMax} onChange={set('payMax')} />
          </label>
          <label className="w-pay-field w-pay-unit">
            <span className="muted small">{tw('w.profile.payPer')}</span>
            <select value={form.payUnit} onChange={set('payUnit')}>
              {PAY_UNITS.map((u) => <option key={u} value={u}>{tw(`w.pay.${u}`)}</option>)}
            </select>
          </label>
        </div>
      </fieldset>

      <label>
        {tw('w.profile.arrangements')}
        <select value={form.arrangement} onChange={set('arrangement')}>
          <option value="">—</option>
          {ARRANGEMENTS.map((a) => <option key={a} value={a}>{tw(`w.arr.${a}`)}</option>)}
        </select>
      </label>

      <label>{tw('w.post.schedule')}<input value={form.schedule} onChange={set('schedule')} maxLength={400} /></label>
      <label>{tw('w.post.location')}<input value={form.location} onChange={set('location')} maxLength={200} /></label>
      <label>{tw('w.post.starts')}<input type="date" value={form.startsOn} onChange={set('startsOn')} /></label>
      <label>{tw('w.post.closes')}<input type="date" value={form.closesOn} onChange={set('closesOn')} /></label>
      <label>
        {tw('w.post.requirements')}
        <textarea value={form.requirements} onChange={set('requirements')} rows={3} maxLength={4000} />
      </label>

      {error && <p className="error">{error}</p>}
      {status === 'draft' ? (
        <>
          <button className="primary" type="button" disabled={busy} onClick={(e) => void submit(e, true)}>
            {busy ? '…' : tw('w.post.saveAndPublish')}
          </button>
          <button type="submit" disabled={busy}>{tw('w.post.save')}</button>
        </>
      ) : (
        <button className="primary" type="submit" disabled={busy}>{busy ? '…' : tw('w.post.saveChanges')}</button>
      )}
    </form>
  )
}

/* ── Applicants ───────────────────────────────────────────────────────────── */

interface Snapshot {
  displayName?: string
  photoId?: string | null
  headline?: string | null
  summary?: string | null
  availability?: string | null
  skills?: string[]
  categories?: string[]
  transport?: string | null
  payMin?: number | null
  payMax?: number | null
  payUnit?: string | null
}

/**
 * The pile of people who applied, one posting at a time.
 *
 * ── Why the job picker is on this screen ────────────────────────────────────
 *
 * The Applicants tab used to show the list of POSTINGS and make you pick one
 * before it would show you a single person. That is a menu standing in front
 * of a screen whose name already said what it was for. The picker is now a
 * line at the top: it opens on the posting with the most people waiting, and
 * changing posting is one tap rather than a detour through another screen.
 *
 * ── Four piles, and why reading the list moves nobody ───────────────────────
 *
 * New means nobody has opened it. That is the one fact on this screen an
 * applicant can see from their side — "your application was viewed" — so it
 * changes only when somebody actually opens one, never because a list was
 * scrolled past. The server enforces that; this screen just never asks.
 */
export function Applicants({
  membership,
  jobId,
  onPickJob,
  onOpenThread,
  onBackToPostings,
}: {
  membership: Membership
  /** The posting to open on, if somebody arrived from a card that named one. */
  jobId: string | null
  onPickJob: (id: string | null) => void
  onOpenThread: (threadId: string) => void
  /** Set when opened from a posting: shows Back beside the heading. */
  onBackToPostings?: () => void
}) {
  const [jobs, setJobs] = useState<JobCard[] | null>(null)
  const [rows, setRows] = useState<Applicant[] | null>(null)
  const [pile, setPile] = useState<Pile>('new')
  const [newestFirst, setNewestFirst] = useState(true)
  const [greatOnly, setGreatOnly] = useState(false)
  const [picking, setPicking] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void worx.employerJobs(membership.employerId)
      .then((r) => setJobs(r.jobs))
      .catch(() => setJobs([]))
  }, [membership.employerId])

  /*
   * Which posting we are looking at.
   *
   * Whatever was asked for, else the one with the most people waiting to be
   * read — which is the posting an employer opening this screen almost always
   * meant, and the only ordering that does not need them to remember which
   * job was which.
   */
  const job =
    jobs?.find((j) => j.id === jobId) ??
    [...(jobs ?? [])].sort((a, b) => (b.unreviewed ?? 0) - (a.unreviewed ?? 0) || (b.applicants ?? 0) - (a.applicants ?? 0))[0] ??
    null

  // The ID, not the object: `job` is derived from `jobs` on every render, so
  // depending on it would refetch the list every time anything re-renders.
  const openJobId = job?.id ?? null
  const load = useCallback(async () => {
    if (!openJobId) return
    try {
      setRows((await worx.applicants(openJobId)).applications)
    } catch {
      setRows([])
    }
  }, [openJobId])

  useEffect(() => { void load() }, [load])

  async function move(id: string, status: string) {
    setError(null)
    try {
      await worx.setApplicationStatus(id, status)
      if (status === 'hired') celebrate({ title: tw('w.cheer.hired'), line: tw('w.cheer.hiredLine') })
      else done()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.work'))
    }
  }

  if (!jobs) return <p className="muted">…</p>
  if (!jobs.length) return <p className="muted w-empty">{tw('w.ap.noJobs')}</p>
  if (!job) return <p className="muted w-empty">{tw('w.ap.noJobs')}</p>

  if (open && rows) {
    const found = rows.find((a) => a.id === open)
    if (found) {
      return (
        <Review
          application={found}
          onBack={() => { setOpen(null); void load() }}
          onMove={(status) => void move(found.id, status)}
          onOpenThread={onOpenThread}
        />
      )
    }
  }

  const counts = {
    new: (rows ?? []).filter((a) => pileOf(a) === 'new').length,
    reviewed: (rows ?? []).filter((a) => pileOf(a) === 'reviewed').length,
    interview: (rows ?? []).filter((a) => pileOf(a) === 'interview').length,
    decided: (rows ?? []).filter((a) => pileOf(a) === 'decided').length,
  }

  const shown = (rows ?? [])
    .filter((a) => pileOf(a) === pile)
    .filter((a) => !greatOnly || (a.match?.score ?? 0) >= MATCH_MIN)
    .sort((a, b) => (newestFirst ? b.createdAt - a.createdAt : a.createdAt - b.createdAt))

  return (
    <div className="w-feed">
      {/* Back sits in the heading row, on the right, rather than in the bar. */}
      <div className="w-heading-row">
        <h1 className="w-feed-heading">{tw('w.ap.title')}</h1>
        {onBackToPostings && <BackButton onBack={onBackToPostings} />}
      </div>

      {/* The posting, as a line you can change rather than a screen you came
          from. */}
      <button className="w-picker" onClick={() => setPicking(true)} aria-expanded={picking}>
        <Logo name={job.employerName} src={job.employerLogoId} className="w-picker-logo" />
        <span className="w-picker-text">
          <b>{job.title}</b>
          <span className="muted"> · {job.applicants ?? 0} {(job.applicants ?? 0) === 1 ? tw('w.ap.one') : tw('w.ap.many')}</span>
        </span>
        <Icon name="chevron" size={20} className="w-picker-caret" />
      </button>

      {picking && (
        <>
          <div className="w-scrim" onClick={() => setPicking(false)} aria-hidden="true" />
          <div className="w-sheet" role="dialog" aria-modal="true" aria-label={tw('w.ap.pickJob')}>
            <header>
              <h2>{tw('w.ap.pickJob')}</h2>
              <button className="link" onClick={() => setPicking(false)}>{tw('w.feed.done')}</button>
            </header>
            <div className="w-rows">
              {jobs.map((j) => (
                <button
                  key={j.id}
                  className="w-row"
                  onClick={() => { onPickJob(j.id); setPicking(false); setPile('new') }}
                >
                  <span className="w-row-ic"><Icon name="briefcase" size={20} /></span>
                  <span className="w-row-text">
                    <span className="w-row-title">{j.title}</span>
                    <span className="w-row-sub">
                      {j.applicants ?? 0} {(j.applicants ?? 0) === 1 ? tw('w.ap.one') : tw('w.ap.many')}
                      {Boolean(j.unreviewed) && ` · ${j.unreviewed} ${tw('w.applicants.new')}`}
                    </span>
                  </span>
                  {j.id === job.id && <Icon name="check" size={20} className="w-row-go" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="w-fchips" role="group" aria-label={tw('w.ap.title')}>
        {(['new', 'reviewed', 'interview', 'decided'] as const).map((p) => (
          <button
            key={p}
            className={pile === p ? 'w-fchip on' : 'w-fchip'}
            aria-pressed={pile === p}
            onClick={() => setPile(p)}
          >
            {tw(`w.ap.${p}` as never)}
            {/* Coral, and only on New: it is the only pile where the number is
                a queue rather than a record. */}
            <span className={p === 'new' && counts.new > 0 && pile !== p ? 'w-fchip-n w-fchip-n-due' : 'w-fchip-n'}>
              {counts[p]}
            </span>
          </button>
        ))}
      </div>

      <div className="w-sortbar">
        <button className="link w-sort" onClick={() => setNewestFirst((v) => !v)}>
          {newestFirst ? tw('w.ap.newest') : tw('w.ap.oldest')}
          <Icon name="chevron" size={15} className="w-sort-caret" />
        </button>
        <button
          className={greatOnly ? 'link w-sort on' : 'link w-sort'}
          aria-pressed={greatOnly}
          onClick={() => setGreatOnly((v) => !v)}
        >
          <Icon name="funnel" size={16} />
          {greatOnly ? tw('w.ap.greatOnly') : tw('w.ap.filter')}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {rows === null && <p className="muted">…</p>}
      {rows !== null && shown.length === 0 && <p className="muted w-empty">{tw('w.ap.noneHere')}</p>}

      {shown.map((a) => (
        <ApplicantCard key={a.id} application={a} onReview={() => setOpen(a.id)} />
      ))}
    </div>
  )
}

type Pile = 'new' | 'reviewed' | 'interview' | 'decided'

/** Which pile an application sits in. The four are exhaustive by construction. */
function pileOf(a: Applicant): Pile {
  if (a.status === 'applied') return 'new'
  if (a.status === 'interview') return 'interview'
  if (a.status === 'hired' || a.status === 'not_selected' || a.status === 'withdrawn') return 'decided'
  return 'reviewed'
}

/** The snapshot, or an empty one. Never throws — see 0104_applications.sql. */
function snapshotOf(raw: string): Snapshot {
  try {
    return JSON.parse(raw) as Snapshot
  } catch {
    return {}
  }
}

function ApplicantCard({ application, onReview }: { application: Applicant; onReview: () => void }) {
  const snap = snapshotOf(application.snapshot)
  const great = (application.match?.score ?? 0) >= MATCH_MIN
  const photo = imageUrl(snap.photoId)

  return (
    <article className="w-apcard">
      {photo
        ? <img className="w-avatar" src={photo} alt="" width={56} height={56} loading="lazy" decoding="async" />
        : <span className="w-avatar" aria-hidden="true">{initials(snap.displayName ?? '?')}</span>}

      <div className="w-apcard-main">
        <div className="w-apcard-top">
          <h3 className="w-apcard-name">{snap.displayName ?? tw('w.ap.applicant')}</h3>
          {/* One badge, not two: "great match" outranks "new", because it is
              the reason to open this one first. */}
          {great ? (
            <span className="w-stat w-stat-contacted" title={reasons(application.match?.reasons)}>
              <span className="w-stat-dot" aria-hidden="true" /> {tw('w.ap.greatMatch')}
            </span>
          ) : application.viewedAt ? null : (
            <span className="w-stat w-stat-viewed">
              <span className="w-stat-dot" aria-hidden="true" /> {tw('w.ap.new')}
            </span>
          )}
          <span className="w-apcard-when">{ago(application.createdAt)}</span>
        </div>

        {snap.headline && <p className="w-apcard-headline">{snap.headline}</p>}
        <p className="w-apcard-sub">
          {[snap.availability, snap.transport ? tw(`w.transport.${snap.transport}` as never) : null]
            .filter(Boolean)
            .join(' · ')}
        </p>

        {Boolean(snap.skills?.length) && (
          <p className="w-skills">
            {snap.skills!.slice(0, 3).map((k) => <span key={k} className="w-skill">{k}</span>)}
          </p>
        )}
      </div>

      <div className="w-apcard-foot">
        <button className="w-cta" onClick={onReview}>
          {tw('w.ap.review')} <Icon name="chevron" size={18} />
        </button>
      </div>
    </article>
  )
}

/** What the match was made of, for the badge's tooltip. */
function reasons(list?: ('category' | 'arrangement' | 'pay')[]): string | undefined {
  if (!list?.length) return undefined
  return list.map((r) => tw(`w.feed.why.${r}` as never)).join(' · ')
}

/**
 * One application, opened.
 *
 * Opening this is what sets `viewed` — the server does it, on this request,
 * and it is the same fact the applicant is shown as "your application was
 * viewed". So this screen is also the promise that somebody actually read it.
 */
function Review({
  application, onBack, onMove, onOpenThread,
}: {
  application: Applicant
  onBack: () => void
  onMove: (status: string) => void
  onOpenThread: (threadId: string) => void
}) {
  const snap = snapshotOf(application.snapshot)
  const photo = imageUrl(snap.photoId)

  useEffect(() => {
    // Marks it viewed, once, on the server. Nothing here needs the response.
    void worx.application(application.id).catch(() => {})
  }, [application.id])

  return (
    <div className="w-stack">
      <BackRow onBack={onBack} />

      <section className="w-apcard">
        {photo
          ? <img className="w-avatar" src={photo} alt="" width={56} height={56} />
          : <span className="w-avatar" aria-hidden="true">{initials(snap.displayName ?? '?')}</span>}
        <div className="w-apcard-main">
          <h2 className="w-apcard-name">{snap.displayName ?? tw('w.ap.applicant')}</h2>
          {snap.headline && <p className="w-apcard-headline">{snap.headline}</p>}
          <p className="w-apcard-sub">{tw('w.ap.applied')} {ago(application.createdAt)}</p>
        </div>
      </section>

      <section className="card w-fields">
        <ul className="w-cfacts">
          {snap.availability && (
            <li className="w-cfact w-cfact-wide">
              <span className="w-cfact-ic"><Icon name="clock" size={16} /></span>
              {snap.availability}
            </li>
          )}
          {snap.transport && (
            <li className="w-cfact w-cfact-wide">
              <span className="w-cfact-ic"><Icon name="car" size={16} /></span>
              {tw(`w.transport.${snap.transport}` as never)}
            </li>
          )}
          {(snap.payMin ?? snap.payMax) && (
            <li className="w-cfact w-cfact-wide">
              <span className="w-cfact-ic"><Icon name="money" size={16} /></span>
              {payLabel({ payMin: snap.payMin ?? null, payMax: snap.payMax ?? null, payUnit: snap.payUnit ?? null })}
            </li>
          )}
          {application.availableFrom && (
            <li className="w-cfact w-cfact-wide">
              <span className="w-cfact-ic"><Icon name="calendar" size={16} /></span>
              {tw('w.jobs.starts')} {application.availableFrom}
            </li>
          )}
        </ul>

        {Boolean(snap.skills?.length) && (
          <p className="w-skills">
            {snap.skills!.map((k) => <span key={k} className="w-skill">{k}</span>)}
          </p>
        )}

        {application.note && (
          <>
            <h3>{tw('w.ap.note')}</h3>
            <p className="w-prose">{application.note}</p>
          </>
        )}
      </section>

      <section className="card w-fields">
        <h3>{tw('w.ap.move')}</h3>
        <div className="w-opts">
          {STATUSES.filter((x) => x !== application.status).map((x) => (
            <button key={x} type="button" className="w-opt" onClick={() => onMove(x)}>
              {tw(`w.status.${x}`)}
            </button>
          ))}
        </div>
        {application.threadId && (
          <button className="w-cta" onClick={() => onOpenThread(application.threadId!)}>
            <Icon name="chat" size={18} /> {tw('w.applicants.message')}
          </button>
        )}
      </section>

      {/* The employer's side of the same right: an application can be abusive
          too, and the person reading it needs somewhere to say so. */}
      <Report subjectType="worker" subjectId={application.id} label={tw('w.report.person')} />
    </div>
  )
}
